// The fonts every TribeX editor offers: the common ones, a curated set of open-licence web fonts loaded on demand, and (in
// Chrome and Edge) the fonts installed on the computer. The editors' font boxes list all of them; see Shared/icons/../FONTS notes
// in EnterpriseReader/web/FONTS.md for the licences.

export const STANDARD_FONTS = ["Helvetica", "Times New Roman", "Courier"] as const;

export interface OnlineFont {
    family: string;
    /** Fontsource package id. */
    id: string;
    kind: "Sans" | "Serif" | "Mono" | "Display" | "Handwriting";
    licence: "OFL-1.1" | "Apache-2.0";
    /** Has italic styles. */
    italic: boolean;
}

/** Curated: every one is under the SIL Open Font License 1.1 or Apache 2.0. See FONTS.md. */
export const ONLINE_FONTS: OnlineFont[] = [
    { family: "Roboto", id: "roboto", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Open Sans", id: "open-sans", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Lato", id: "lato", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Montserrat", id: "montserrat", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Inter", id: "inter", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Poppins", id: "poppins", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Source Sans 3", id: "source-sans-3", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Noto Sans", id: "noto-sans", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Raleway", id: "raleway", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Nunito", id: "nunito", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "PT Sans", id: "pt-sans", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Fira Sans", id: "fira-sans", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Work Sans", id: "work-sans", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "IBM Plex Sans", id: "ibm-plex-sans", kind: "Sans", licence: "OFL-1.1", italic: true },
    { family: "Merriweather", id: "merriweather", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "Lora", id: "lora", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "Playfair Display", id: "playfair-display", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "Noto Serif", id: "noto-serif", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "PT Serif", id: "pt-serif", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "Libre Baskerville", id: "libre-baskerville", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "EB Garamond", id: "eb-garamond", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "Crimson Text", id: "crimson-text", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "IBM Plex Serif", id: "ibm-plex-serif", kind: "Serif", licence: "OFL-1.1", italic: true },
    { family: "Source Code Pro", id: "source-code-pro", kind: "Mono", licence: "OFL-1.1", italic: true },
    { family: "Roboto Mono", id: "roboto-mono", kind: "Mono", licence: "OFL-1.1", italic: true },
    { family: "IBM Plex Mono", id: "ibm-plex-mono", kind: "Mono", licence: "OFL-1.1", italic: true },
    { family: "JetBrains Mono", id: "jetbrains-mono", kind: "Mono", licence: "OFL-1.1", italic: true },
    { family: "Inconsolata", id: "inconsolata", kind: "Mono", licence: "OFL-1.1", italic: false },
    { family: "Oswald", id: "oswald", kind: "Display", licence: "OFL-1.1", italic: false },
    { family: "Bebas Neue", id: "bebas-neue", kind: "Display", licence: "OFL-1.1", italic: false },
    { family: "Dancing Script", id: "dancing-script", kind: "Handwriting", licence: "OFL-1.1", italic: false },
    { family: "Caveat", id: "caveat", kind: "Handwriting", licence: "OFL-1.1", italic: false }
];

export const FONT_CDN = "https://cdn.jsdelivr.net/fontsource/fonts";
export const onlineFont = (family: string) => ONLINE_FONTS.find((f) => f.family === family);

export const isStandardFont = (family: string) => !onlineFont(family) && !localFonts.has(family);

/** The local font records, for the Reader's embedding code. */
export const localFontRecords = (family: string) => localFonts.get(family);

/** The CSS font-family list for a family, with a fallback so text never disappears while a font loads. */
export function cssFamily(family: string): string {
    if (/^courier/i.test(family)) return '"Courier New", Courier, monospace';
    if (/^times/i.test(family)) return '"Times New Roman", Times, serif';
    if (/^helvetica$|^arial$/i.test(family)) return "Arial, Helvetica, sans-serif";
    const o = onlineFont(family);
    const fallback = o?.kind === "Serif" ? "serif" : o?.kind === "Mono" ? "monospace" : "sans-serif";
    return `"${family}", ${fallback}`;
}

/** A canvas/CSS font shorthand, used to measure the text the way the page will draw it. */
export const cssFont = (size: number, family: string, bold: boolean, italic: boolean) => `${italic ? "italic " : ""}${bold ? 700 : 400} ${size}px ${cssFamily(family)}`;

// ---------- fonts installed on this computer ----------

export interface LocalFontData { family: string; style: string; fullName: string; blob(): Promise<Blob> }
const localFonts = new Map<string, LocalFontData[]>();
let localListeners: Array<() => void> = [];

export const localFontsSupported = () => typeof window !== "undefined" && "queryLocalFonts" in window;
export const localFamilies = () => [...localFonts.keys()].filter((f) => !onlineFont(f)).sort((a, b) => a.localeCompare(b));
export function onLocalFonts(f: () => void): () => void { localListeners.push(f); return () => { localListeners = localListeners.filter((x) => x !== f); }; }

/** Asks the browser for the installed fonts (it prompts the first time). Resolves to the family names. */
export async function loadLocalFonts(): Promise<string[]> {
    const q = (window as unknown as { queryLocalFonts?: () => Promise<LocalFontData[]> }).queryLocalFonts;
    if (!q) throw new Error("This browser can't list the fonts installed on this computer. Chrome and Edge can.");
    const all = await q.call(window);
    localFonts.clear();
    for (const f of all) { const l = localFonts.get(f.family) ?? []; l.push(f); localFonts.set(f.family, l); }
    for (const l of localListeners) l();
    return localFamilies();
}

// ---------- showing a web font on the page ----------

const shown = new Set<string>();

/** Makes the browser load an online font so text boxes and the editor draw it. Resolves when it is ready (or failed). */
export async function showFont(family: string): Promise<void> {
    const o = onlineFont(family);
    if (!o || shown.has(family) || typeof FontFace === "undefined") return;
    shown.add(family);
    const faces: Array<[number, "normal" | "italic"]> = [[400, "normal"], [700, "normal"], ...(o.italic ? ([[400, "italic"], [700, "italic"]] as Array<[number, "italic"]>) : [])];
    await Promise.all(faces.map(async ([weight, style]) => {
        try {
            const f = new FontFace(family, `url(${FONT_CDN}/${o.id}@latest/latin-${weight}-${style}.woff2) format("woff2")`, { weight: String(weight), style });
            document.fonts.add(await f.load());
        } catch { /* the fallback family draws instead */ }
    }));
}

/** Fonts people expect to find in a word processor, a spreadsheet or a slide editor (installed on most Windows and Mac computers). */
export const COMMON_FONTS = ["Calibri", "Cambria", "Arial", "Times New Roman", "Georgia", "Verdana", "Tahoma", "Trebuchet MS", "Segoe UI", "Consolas", "Courier New", "Comic Sans MS", "Garamond", "Palatino Linotype", "Impact", "Helvetica"];

export interface FontOption { value: string; label: string; group: string }

/** Every font for a font box: common, online, installed. The current font is added when it is none of those. */
export function fontOptions(current?: string): FontOption[] {
    const all: FontOption[] = [
        ...COMMON_FONTS.map((f) => ({ value: f, label: f, group: "Common" })),
        ...(["Sans", "Serif", "Mono", "Display", "Handwriting"] as const).flatMap((k) => ONLINE_FONTS.filter((f) => f.kind === k).map((f) => ({ value: f.family, label: f.family, group: `${k} · online, open licence` }))),
        ...localFamilies().filter((f) => !COMMON_FONTS.includes(f)).map((f) => ({ value: f, label: f, group: "On this computer" }))
    ];
    return current && !all.some((o) => o.value === current) ? [{ value: current, label: current, group: "In this document" }, ...all] : all;
}

/** A footer entry for a font box that offers the installed fonts, or null when the browser can't list them or they are listed already. */
export function localFontsFooter(): { label: string; run(): Promise<void> } | null {
    return localFontsSupported() && localFamilies().length === 0 ? { label: "Show fonts installed on this computer…", run: async () => { await loadLocalFonts(); } } : null;
}
